import { useId, type ComponentPropsWithRef } from "react";
import { cx } from "../../cx";
import { inputStateClasses } from "../Input/Input";

export interface TextareaProps extends ComponentPropsWithRef<"textarea"> {
  /** Visible label (required for accessibility). */
  readonly label: string;
  /** Visually hide the label but keep it for screen readers. */
  readonly hideLabel?: boolean;
  readonly hint?: string;
  /** Error message; also sets `aria-invalid`. */
  readonly error?: string;
  readonly containerClassName?: string;
}

/** Multi-line text field styled like `Input` (same tokens, focus ring and error state). */
export function Textarea({
  label,
  hideLabel = false,
  hint,
  error,
  id,
  className,
  containerClassName,
  rows = 3,
  ...rest
}: TextareaProps) {
  const autoId = useId();
  const fieldId = id ?? `textarea-${autoId}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cx("flex flex-col gap-1.5", containerClassName)}>
      <label
        htmlFor={fieldId}
        className={cx("text-sm font-medium text-fg", hideLabel && "sr-only")}
      >
        {label}
      </label>
      <textarea
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cx(
          "block w-full rounded-xl border px-3.5 py-2.5 text-sm transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
          error ? inputStateClasses.error : inputStateClasses.base,
          className,
        )}
        {...rest}
      />
      {error && (
        <p id={errorId} className="text-xs text-danger-fg">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
